import React, { useRef } from 'react';
import CrudListPage from './CrudListPage';
import SectionCard from '../ui/SectionCard';
import { Button } from '../ui/Button';
import PageHeader from '../ui/PageHeader';
import { PlusIcon, BuildingStorefrontIcon, InboxStackIcon } from '@heroicons/react/24/outline';

/**
 * VendorsWarehousesPage — Settings › Vendors & Warehouses (Iter18 §2.14).
 *
 * Two separate lists on one page:
 *   • Vendors    — NAME ONLY (no address, phone, or contact fields).
 *   • Warehouses — NAME + STREET + CITY + STATE + ZIP (all required, R2 §1.14).
 *
 * SUPERSESSION NOTE (2026-07-31):
 *   Iter18 Polish Round 2 rev 5 (2026-07-17) scoped `SectionCard` to Customer
 *   Types only. That was a Round-2 pacing decision, not a structural boundary.
 *
 *   Superseded 2026-07-31 (§3.1 SectionHeader wave, DEC-48 "build components
 *   not pages"): the SectionCard pattern now applies to all six settings
 *   surfaces. This page mounts <SectionCard> ×2 to match the Customer Types
 *   reference. Reason: scope-expansion-by-operator, not error correction.
 *
 *   See iter18-settings-polish-round-2.md §0.4 / §1.14 supersession footnote.
 *
 * Downstream propagation: records added here appear in the app's quote-item
 * vendor/warehouse dropdowns.
 */
export default function VendorsWarehousesPage() {
  const vendorsRef = useRef(null);
  const warehousesRef = useRef(null);

  const addButton = (ref, label) => (
    <Button
      variant="secondary"
      size="sm"
      onClick={() => ref.current?.openAdd()}
    >
      <PlusIcon className="h-4 w-4 mr-1" aria-hidden="true" />
      {label}
    </Button>
  );

  return (
    <div>
      <PageHeader
        title="Vendors & Warehouses"
        description="Vendors used on quote items and warehouse locations that stock materials."
      />

      <div className="space-y-4">
        <SectionCard
          title="Vendors"
          description="Material vendors used on quote items. Selected as the default source for line items."
          icon={BuildingStorefrontIcon}
          actions={addButton(vendorsRef, 'Add')}
        >
          <CrudListPage
            ref={vendorsRef}
            bare
            listType="vendors"
            fields={[
              { key: 'name', label: 'Vendor Name', type: 'text', required: true, placeholder: 'e.g., ABC Supply' },
            ]}
            emptyMessage="No vendors configured. Add vendors that appear as the default source for quote items."
          />
        </SectionCard>

        <SectionCard
          title="Warehouses"
          description="Warehouse locations where inventory is stored. Used to route quote-item stock and job dispatch."
          icon={InboxStackIcon}
          actions={addButton(warehousesRef, 'Add')}
        >
          <CrudListPage
            ref={warehousesRef}
            bare
            listType="warehouses"
            fields={[
              { key: 'name', label: 'Warehouse Name', type: 'text', required: true, placeholder: 'e.g., Main Warehouse' },
              { key: 'street', label: 'Street', type: 'text', required: true, placeholder: '123 Main St' },
              { key: 'city', label: 'City', type: 'text', required: true, placeholder: 'City' },
              { key: 'state', label: 'State', type: 'text', required: true, placeholder: 'State' },
              { key: 'zip', label: 'ZIP', type: 'text', required: true, placeholder: 'ZIP' },
            ]}
            emptyMessage="No warehouses configured. Add warehouses to route quote-item stock and job dispatch."
          />
        </SectionCard>
      </div>
    </div>
  );
}
